/**
 * Kalenderrechnung auf reinen Datumswerten.
 *
 * ⚠️ Bewusst OHNE `Date`: `new Date('2026-03-29')` wird als UTC gelesen und
 * kippt in negativen Zeitzonen auf den Vortag. Kalenderarithmetik hat mit
 * Zeitzonen nichts zu tun - die kommen erst ins Spiel, wenn ein Datum mit einer
 * Uhrzeit verbunden wird (siehe zeit.ts).
 */

/** Datum als `YYYY-MM-DD`. */
export type Datum = string

export interface Ymd {
  jahr: number
  monat: number // 1..12
  tag: number // 1..31
}

const zwei = (n: number): string => String(n).padStart(2, '0')

export function zerlege(d: Datum): Ymd {
  const t = d.split('-')
  const jahr = Number(t[0])
  const monat = Number(t[1])
  const tag = Number(t[2])
  if (!Number.isInteger(jahr) || !Number.isInteger(monat) || !Number.isInteger(tag)) {
    throw new Error(`Kein gültiges Datum: ${d}`)
  }
  return { jahr, monat, tag }
}

export const setze = ({ jahr, monat, tag }: Ymd): Datum =>
  `${String(jahr).padStart(4, '0')}-${zwei(monat)}-${zwei(tag)}`

export const istSchaltjahr = (j: number): boolean => (j % 4 === 0 && j % 100 !== 0) || j % 400 === 0

const TAGE = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

export function tageImMonat(jahr: number, monat: number): number {
  if (monat === 2 && istSchaltjahr(jahr)) return 29
  return TAGE[monat - 1] ?? 30
}

/** Tage seit dem 1. Januar 1970, ohne Zeitzone. */
export function alsTagzahl(d: Datum): number {
  const { jahr, monat, tag } = zerlege(d)
  // Howard Hinnants days_from_civil - exakt, ohne Gleitkomma.
  const y = monat <= 2 ? jahr - 1 : jahr
  const aera = Math.floor(y / 400)
  const jia = y - aera * 400
  const tia = Math.floor((153 * (monat + (monat > 2 ? -3 : 9)) + 2) / 5) + tag - 1
  const tja = jia * 365 + Math.floor(jia / 4) - Math.floor(jia / 100) + tia
  return aera * 146097 + tja - 719468
}

export function ausTagzahl(z: number): Datum {
  const t = z + 719468
  const aera = Math.floor(t / 146097)
  const ta = t - aera * 146097
  const jia = Math.floor((ta - Math.floor(ta / 1460) + Math.floor(ta / 36524) - Math.floor(ta / 146096)) / 365)
  const y = jia + aera * 400
  const tja = ta - (365 * jia + Math.floor(jia / 4) - Math.floor(jia / 100))
  const mp = Math.floor((5 * tja + 2) / 153)
  const tag = tja - Math.floor((153 * mp + 2) / 5) + 1
  const monat = mp + (mp < 10 ? 3 : -9)
  return setze({ jahr: monat <= 2 ? y + 1 : y, monat, tag })
}

export const plusTage = (d: Datum, n: number): Datum => ausTagzahl(alsTagzahl(d) + n)

/** ISO-Wochentag: 1 = Montag … 7 = Sonntag. */
export function wochentag(d: Datum): number {
  // 1970-01-01 war ein Donnerstag (ISO 4).
  const rest = (alsTagzahl(d) + 3) % 7
  return (rest < 0 ? rest + 7 : rest) + 1
}

/** Montag der Woche, in der `d` liegt. */
export const wochenStart = (d: Datum): Datum => plusTage(d, -(wochentag(d) - 1))

export const monatsIndex = (d: Datum): number => {
  const { jahr, monat } = zerlege(d)
  return jahr * 12 + (monat - 1)
}

export const ausMonatsIndex = (i: number): { jahr: number; monat: number } => ({
  jahr: Math.floor(i / 12),
  monat: (i % 12) + 1,
})

/**
 * Ist das ein Tag, den es im Kalender wirklich gibt?
 *
 * ⚠️ Die FORM allein genügt nicht: `2026-02-30` passt auf jedes
 * `\d{4}-\d{2}-\d{2}`, und ohne diese Prüfung landet es in der Datenbank -
 * die es ablehnt, woraufhin der Mensch statt „Eingabe ungültig" einen
 * Serverfehler sieht.
 *
 * Bewusst ohne `Date` wie der Rest dieser Datei.
 */
export function istDatum(wert: unknown): wert is Datum {
  if (typeof wert !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(wert)) return false
  const { jahr, monat, tag } = zerlege(wert)
  if (monat < 1 || monat > 12) return false
  return tag >= 1 && tag <= tageImMonat(jahr, monat)
}
